import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAddressStore } from '../../../stores/address';
import { useInitializationDataContext } from '../../../context/data-context/data-context';
import { EnvelopeType } from '../../../global-types/envelope';
import useAddressSelection from '../../../hooks/use-address-selection';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { updateProject } from '../../../services/customization';
import { getEnvelopeCode } from '../../../utils/address/get-envelope-code';
import { useGetProjectCurrentEnvelope } from '../../address/hooks/use-get-project-current-envelope';
import { useEditorNavigation } from '../../editor/hooks/use-editor-navigation';
import { EnvelopeSelectionForm } from '../select-envelope-types';

export const useEnvelopeSelection = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const cleanAddresses = useAddressStore((state) => state.cleanAddresses);
  const { handleResetAddresses } = useAddressSelection();
  const [searchParams] = useSearchParams(search);
  const { handleIndexChange } = useEditorNavigation();
  const { IS_EDITOR_HEADER_ENABLED } = useFeatureFlags();
  const { getProjectCurrentEnvelopeType } = useGetProjectCurrentEnvelope();
  const projectId = initializedData?.project_id;

  const getEnvelopeSelection = () => searchParams.get('envelope');

  const envelopeForm = useForm<EnvelopeSelectionForm>({
    mode: 'onChange',
    defaultValues: { envelopeOption: (getEnvelopeSelection() as EnvelopeType) || undefined },
  });

  const selectedEnvelope = envelopeForm.watch('envelopeOption') || getEnvelopeSelection();

  const handleBack = () => {
    const lastProjectLoaded = sessionStorage.getItem('lastProjectLoaded');

    if (!lastProjectLoaded && !projectId) return;

    searchParams.delete('envelope');
    navigate({
      pathname: lastProjectLoaded
        ? `/card/customization/edit/${lastProjectLoaded}`
        : `/card/customization/edit/${projectId}`,
      search: searchParams.toString(),
    });
    if (!IS_EDITOR_HEADER_ENABLED) {
      handleIndexChange(0, 0);
    }
  };

  const handleContinue = () => {
    if (!selectedEnvelope) return;

    searchParams.set('envelope', selectedEnvelope);
    // Call /update API to update project's envelope type, then navigate
    const data = {
      envelope_type: getEnvelopeCode(selectedEnvelope),
    };
    return new Promise<void>((resolve, reject) => {
      updateProject(data, projectId)
        .then(() => {
          const isBlankEnvelope = selectedEnvelope === EnvelopeType.Blank;
          const isReturnEnvelope = selectedEnvelope === EnvelopeType.Return;

          if (isBlankEnvelope) {
            cleanAddresses(); // reset addresses for 1:1
            handleResetAddresses({ envelope: EnvelopeType.Blank }); // reset addresses for 1:M
          }

          isReturnEnvelope && handleResetAddresses({ envelope: EnvelopeType.Return });

          // navigateToPreview is used to navigate to the preview view when
          // the card is 1:M and the feature flags are disabled.
          // It is used just to navigate to the preview view without any validation.
          const pathToNavigate = isBlankEnvelope ? '/card/customization/preview' : '/card/customization/address';
          navigate({ pathname: pathToNavigate, search: searchParams.toString() });
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  };

  useEffect(() => {
    envelopeForm.setValue('envelopeOption', getProjectCurrentEnvelopeType());
  }, []);

  return {
    envelopeForm,
    selectedEnvelope,
    getEnvelopeSelection,
    handleBack,
    handleContinue,
  };
};
